/*
tslint:disable:import-spacing no-inferrable-types
variable-name align no-trailing-whitespace component-class-suffix
no-string-literal component-selector
*/

import 'ag-grid-enterprise';

import {TrainClass} from '@/models/scenario/train.class';

import * as $ from 'jquery';

import 'select2';
import {OptGroupData, OptionData, SearchOptions} from 'select2';
import KeyDownEvent = JQuery.KeyDownEvent;
import KeyUpEvent = JQuery.KeyUpEvent;
import {ICellEditorComp, ICellEditorParams} from "ag-grid-community";

/**
 *
 * @class TrainClassesEditor
 *
 * @brief dynamic dropdown list for train classes, also allows for type ahead on decision
 * point name.
 *
 * We use Select2: https://select2.org  for the "combo box".
 *
 * @note The select2 @types/select2 (definitely typed stuff), includes "requirejs" which is
 * not going to be compatible with Angular.  Just edit its index.d.ts file and remove the
 * requirejs stuff after you add it to your npm repo.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      August 2020
 *
 */
export class TrainClassesEditor implements ICellEditorComp {

  /** agGrid DOM element for display */
  public gui: HTMLElement;

  /** the selectable values (classes) */
  public values: TrainClass[] = [];

  /** the selected train class */
  public value: TrainClass = null;

  /** the callback to tell agGrid we're done editing */
  private editingDone: (suppressNavigateAfterEdit?: boolean) => void = null;

  private clickListenter: (e: MouseEvent) => void = null;

  /** set up the custom editor (happens on every edit attempt) */
  public init(params: ICellEditorParams): void {

    const tc         = this;
    tc.editingDone   = params.stopEditing;

    tc.values        = [];
    tc.value         = params.value;

    /* get the total set of train classes... */

    // @ts-ignore
    tc.values = params.trainClasses();

    /* set up the Select2 drop down ... */

    tc.gui   = document.createElement('div');
    tc.gui.tabIndex = 0;

    if (!tc.values || (tc.values.length === 0)) {

      /* nothing to do, we don't have any decision points */
      return ;
    }

    const   data: any[]         = [];

    /* walk the train classes, generating a select option for each */

    for (const trainClass of tc.values) {

      /* build this option ... */

      const name = trainClass._name.trim().toUpperCase();
      const dir = TrainClass.typeToString(trainClass._dir);

      let selected: boolean = false;

      /* is this the current value? */

      if (tc.value instanceof TrainClass) {

        selected = trainClass._id === tc.value._id;
      }

      /* add the option */

      data.push({
        id: `tc-${trainClass._id}`,
        text: `${name} (${dir})`,
        name,
        tc: trainClass,
        selected
      });

    }

    /* insert new HTML for our custom select ... */

    const html = `<select class="train-class-select"><option></option></select>`;
    $(tc.gui).html(html);

    /* now create a Select2 version of it, that supports both selection and type ahead ... */

    const el: JQuery = $(tc.gui).find('select.train-class-select');

    $(el).on('select2:close', () => {

      $(el).trigger('focus');

    });

    $(el).on("select2:closed", function() {

      $(el).select2("open");
    });

    $(el).on('select2:select', (e) => {

      const   selBox: JQuery =  $('input.select2-search__field');

      $(selBox).trigger('focus');

      const datum: any           = e.params.data;

      /* update the current value */

      tc.value = datum.tc;

      tc.editingDone(true);
    });

    $(el).select2({

      placeholder:  `Select...`,
      allowClear:    false,
      closeOnSelect: true,
      width:         '200px',

      data,

      /* our type-ahead matching function */
      matcher: (args: SearchOptions, datum: OptGroupData | OptionData): any => {

        /* do we have something to match? */

        if ((args?.term ?? '').trim() === '') {

          return datum;
        }

        // @ts-ignore
        const name: string = datum?.name;

        if (!name) {

          /* likely the placeholder option */

          return null;
        }

        if (name.toUpperCase().indexOf(args.term.toUpperCase()) === 0) {

          return datum;
        }

        return null;
      }

    });

    $(el).on("select2:open", function() {

      /*
       * once the dropdown is open, make sure the selection area has the focus so if you press ENTER
       * we can finish editing.
       *
       */

      const selBox: JQuery = $('input.select2-search__field');

      $(selBox).css('width', 'auto');

      if ((params.eventKey ?? '').length === 1) {

        $(selBox).val(params.eventKey);
      }

      $(selBox).trigger('focus');

    });

    setTimeout(() => {

      $(el).select2('open');

      $(tc.gui).on('keyup', (e: KeyUpEvent) => {

        if (e.key !== 'Tab') {

          return ;
        }

        tc.editingDone(true);

      });

      $(tc.gui).on('keydown', (e: KeyDownEvent) => {

        if ((e.key !== 'Tab') && (e.key !== 'Enter')) {

          return ;
        }

        tc.editingDone(true);

        e.preventDefault();
        e.stopPropagation();
      });

    }, 50);

    /*
     * watch for clicks outside the editor, implies closing the editor; the editor popups
     * are modal, there should only be one at a time.
     *
     */

    tc.clickListenter = (e: MouseEvent) => {

      let target = e.target;

      do {

        if ($(target).hasClass(`select2-container`)) {

          /* it's ok, somewhere inside */

          return;
        }

        if ($(target).hasClass('select2-selection__choice')) {

          /* it's ok, somewhere inside */

          return;
        }

        if (target === tc.gui) {

          /* it's ok, somewhere inside */

          return;
        }

        // @ts-ignore
        target = target.parentNode;

      } while (target);

      /* click is somewhere outside, auto-close */

      document.removeEventListener('click', tc.clickListenter);

      tc.editingDone(true);
    };

    document.addEventListener('click', tc.clickListenter);
  }

  /** focus on local editing control (after it's attached to agGrid) */
  public afterGuiAttached(): void {

  }

  /* Return the DOM element of your editor, this is what the grid puts into the DOM */
  public getGui(): HTMLElement {

    return this.gui;
  }

  /* Should return the final value to the grid, the result of the editing */
  public getValue(): TrainClass {

    return this.value;
  }

  /** do any clean up */
  destroy(): void {

    /* clear out our loss of focus listener */

    document.removeEventListener('click', this.clickListenter);

    /* deep six the select drop-down */

    const el: JQuery = $(this.gui).find('select.train-class-select');

    $(el).select2('destroy');

    /* remove the DOM we inserted */

    $(this.gui).remove();
  }

  /** return true to be placed in a popup */
  public isPopup(): boolean {

    return true;
  }

  /** return the positioning, either 'over' or 'under' (only for popups) */
  getPopupPosition(): 'over' | 'under' | undefined {

    return 'over';
  }

  /** return true to disallow editing before it even starts */
  isCancelBeforeStart(): boolean {

    /* if there are no decision points...nothing we can do */

    return this.values.length === 0;
  }

  /** return true to ignore editing change */
  isCancelAfterEnd(): boolean {

    return false;
  }

  /** on tabbing into the cell */
  public focusIn(): boolean {

    return true;
  }

  /** on tabbing out of the cell */
  public focusOut(): boolean {

    return true;
  }
}
